import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import {
  MessageCircleHeart, Brain, ScrollText, HeartPulse, Sparkles, BookOpen, ListChecks, Target,
  Users, Stethoscope, LifeBuoy, HandHeart, TreeDeciduous, ShieldCheck, Settings as SettingsIcon, Shield,
} from "lucide-react";

const SECTIONS = [
  {
    title: "Your everyday companions",
    items: [
      { icon: MessageCircleHeart, to: "/app/companion", name: "Companion", what: "A caring AI you can talk to anytime.", how: "It remembers your past conversations and follows up naturally. After meaningful moments it may ask 'Remember this?' — you decide what it keeps." },
      { icon: HeartPulse, to: "/app/checkin", name: "Daily Check-in", what: "A quick, gentle pulse on how you're doing.", how: "Answer a rotating question and slide a few dials (mood, energy, sleep, stress…). Over time these become your trends." },
      { icon: BookOpen, to: "/app/journal", name: "Journal", what: "A private space to think out loud.", how: "Type or record a voice note. The AI adds a gentle summary, detects the feeling, and tags topics so you can search later." },
    ],
  },
  {
    title: "Understanding yourself over time",
    items: [
      { icon: Sparkles, to: "/app/insights", name: "Insights", what: "Gentle patterns the AI notices.", how: "It explains its reasoning and shows an honest confidence level. It never diagnoses — just reflects what your data suggests." },
      { icon: Brain, to: "/app/memories", name: "Memories", what: "Everything the companion remembers.", how: "You're in full control: add, edit, hide, delete, export, or switch memory off entirely. Nothing is stored without your consent." },
      { icon: ScrollText, to: "/app/lifestory", name: "Life Story", what: "A meaningful timeline of your life's chapters.", how: "Add milestones, losses, breakthroughs and dreams. The companion connects today's moments to how far you've come." },
      { icon: TreeDeciduous, to: "/app/growth", name: "Growth", what: "Healthy motivation, never addictive.", how: "Watch your growth tree, streaks, milestones and kindness score grow as you show up for yourself." },
    ],
  },
  {
    title: "Building better days",
    items: [
      { icon: ListChecks, to: "/app/habits", name: "Habits", what: "Small, personalized rituals.", how: "Add your own or let the AI suggest habits tailored to you — always with the reason behind each one." },
      { icon: Target, to: "/app/goals", name: "Goals", what: "Directions that matter to you.", how: "Set goals like 'sleep better' and nudge your progress at your own pace — no pressure." },
    ],
  },
  {
    title: "Connection & support",
    items: [
      { icon: Users, to: "/app/community", name: "Community", what: "A kind, judgement-free space.", how: "Post anonymously or as yourself. No follower counts or popularity games. Every post is gently watched by AI moderation that explains its decisions and never silently deletes." },
      { icon: Stethoscope, to: "/app/professionals", name: "Find Help", what: "Verified professionals for real support.", how: "Search by specialty, language, country or format, save favourites, and view detailed profiles when you're ready for human care." },
      { icon: HandHeart, to: "/app/circle", name: "Support Circle", what: "The people you trust most.", how: "Add family, friends or your therapist and choose exactly what may be shared. Nothing is sent automatically without your say-so." },
      { icon: LifeBuoy, to: "/app/resources", name: "Resources", what: "Trusted helplines & organisations.", how: "Browse crisis lines and support services filtered to your region and the kind of help you need." },
    ],
  },
  {
    title: "Your control",
    items: [
      { icon: SettingsIcon, to: "/app/settings", name: "Settings", what: "Comfort and control.", how: "Switch between light, dark and high-contrast themes, enable larger text, export all your data, or delete your account entirely." },
    ],
  },
];

export default function Guide() {
  const { t } = useI18n();
  return (
    <div className="max-w-4xl">
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("guide.title")}</h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        EverMind is a warm companion for your mental wellbeing — here to listen, reflect and support you over time.
        Here's what everything does and how to use it. Explore at your own pace; nothing is ever required.
      </p>

      <div className="mt-6 flex items-start gap-3 rounded-2xl bg-primary/5 border border-primary/20 p-4" data-testid="guide-disclaimer">
        <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Important:</span> EverMind is not a therapist, doctor or emergency service.
          It never diagnoses or prescribes, and it will encourage professional help when that's the kind thing to do.
          If you're ever in immediate danger, use the <span className="font-medium text-foreground">"Need help now?"</span> button for crisis resources.
        </p>
      </div>

      {SECTIONS.map((section, si) => (
        <div key={section.title} className="mt-10">
          <h2 className="font-serif text-2xl">{section.title}</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {section.items.map((it, i) => (
              <motion.div key={it.name} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: i * 0.05 }}
                          className="ambient-shadow rounded-3xl border border-border/60 bg-card p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10">
                    <it.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl">{it.name}</h3>
                </div>
                <p className="mt-4 text-sm font-medium">{it.what}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{it.how}</p>
                <Link to={it.to} className="mt-4 inline-block text-sm text-primary hover:underline" data-testid={`guide-open-${it.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  Open {it.name} →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-12 rounded-3xl border border-border/60 bg-gradient-to-b from-accent/30 to-card p-8 text-center">
        <h2 className="font-serif text-2xl">Ready when you are</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">There's no right way to use EverMind. Start with a check-in or a chat, and let the rest unfold.</p>
        <Link to="/app/companion" className="mt-5 inline-block rounded-full bg-primary px-6 py-2.5 text-sm text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5" data-testid="guide-start-companion">
          Talk to your companion
        </Link>
      </div>
    </div>
  );
}
